<template>
    <v-form ref="form" v-model="valid">
        <!-- Seção: Identificação -->
        <div class="section-label">
            <v-icon small class="mr-1 primary--text">mdi-map-marker-outline</v-icon>
            <span class="overline primary--text font-weight-bold">Identificação</span>
        </div>
        <v-divider class="mb-3"></v-divider>
        <v-row dense>
            <v-col cols="12" sm="4" md="3">
                <v-text-field
                    dense outlined label="UF"
                    prepend-inner-icon="mdi-map"
                    v-model="comarca.cd_uf"
                    disabled hide-details
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="3">
                <v-text-field
                    dense outlined label="Código da Comarca"
                    prepend-inner-icon="mdi-identifier"
                    v-model="comarca.cd_ibge_comarca"
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="6">
                <v-text-field
                    dense outlined label="Nome da Comarca"
                    prepend-inner-icon="mdi-city"
                    v-model="comarca.nm_comarca"
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="3">
                <v-text-field
                    dense outlined label="Código do Município"
                    prepend-inner-icon="mdi-identifier"
                    v-model="comarca.cd_ibge_municipio"
                    disabled hide-details
                    class="mt-2"
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="8" md="9">
                <v-text-field
                    dense outlined label="Nome do Município"
                    prepend-inner-icon="mdi-home-city-outline"
                    v-model="comarca.nm_municipio"
                    disabled hide-details
                    class="mt-2"
                ></v-text-field>
            </v-col>
        </v-row>

        <!-- Seção: Faixa de CEP -->
        <div class="section-label mt-4">
            <v-icon small class="mr-1 primary--text">mdi-map-marker-radius-outline</v-icon>
            <span class="overline primary--text font-weight-bold">Faixa de CEP</span>
        </div>
        <v-divider class="mb-3"></v-divider>
        <v-row dense>
            <v-col cols="12" sm="6">
                <v-text-field
                    dense outlined label="CEP Inicial"
                    prepend-inner-icon="mdi-map-marker-left-outline"
                    v-model="comarca.cd_cep_abrangente_inicio"
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
                <v-text-field
                    dense outlined label="CEP Final"
                    prepend-inner-icon="mdi-map-marker-right-outline"
                    v-model="comarca.cd_cep_abrangente_fim"
                    hide-details
                ></v-text-field>
            </v-col>
        </v-row>

        <!-- Seção: Configurações -->
        <div class="section-label mt-4">
            <v-icon small class="mr-1 primary--text">mdi-cog-outline</v-icon>
            <span class="overline primary--text font-weight-bold">Configurações</span>
        </div>
        <v-divider class="mb-3"></v-divider>
        <v-row dense align="center">
            <v-col cols="12" sm="4" md="4">
                <v-text-field
                    dense outlined label="Limite Diário de Envios"
                    prepend-inner-icon="mdi-counter"
                    v-model="comarca.ds_limite_diario"
                    v-maska="'####'"
                    oninput="if(this.value < 0) this.value = 0;"
                    hide-details
                    @focus="focusInput($event)"
                >
                    <template v-slot:append>
                        <div class="d-flex flex-column" style="gap:2px; margin-top:-4px">
                            <v-btn icon x-small color="primary" @click="aumentarLimite" tabindex="-1">
                                <v-icon x-small>mdi-chevron-up</v-icon>
                            </v-btn>
                            <v-btn icon x-small color="primary" @click="diminuirlimite" tabindex="-1">
                                <v-icon x-small>mdi-chevron-down</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="4" class="d-flex align-center pl-4">
                <v-switch
                    color="success" v-model="comarca.st_ativo"
                    inset dense hide-details
                    data-cy="btnStatusComarca" class="mt-0"
                >
                    <template v-slot:label>
                        <span class="body-2">
                            Status:
                            <strong :class="comarca.st_ativo ? 'success--text' : 'error--text'">
                                {{ comarca.st_ativo ? 'Ativo' : 'Inativo' }}
                            </strong>
                        </span>
                    </template>
                </v-switch>
            </v-col>
            <v-col cols="12" sm="4" md="4" class="d-flex align-center pl-4">
                <v-switch
                    color="success" v-model="comarca.requer_endosso"
                    inset dense hide-details class="mt-0"
                >
                    <template v-slot:label>
                        <span class="body-2">
                            Requer Endosso:
                            <strong :class="comarca.requer_endosso ? 'success--text' : 'grey--text'">
                                {{ comarca.requer_endosso ? 'Sim' : 'Não' }}
                            </strong>
                        </span>
                    </template>
                </v-switch>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-2 grey--text" v-bind="attrs" v-on="on">help</v-icon>
                    </template>
                    <span>Selecione para definir se a comarca requer endosso ou não</span>
                </v-tooltip>
            </v-col>
        </v-row>
    </v-form>
</template>



<style scoped>
.section-label {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}
</style>

<script>
import { required, email, max } from 'vee-validate/dist/rules';
import { extend, setInteractionMode } from 'vee-validate';

setInteractionMode('eager');

extend('required', {
    ...required,
    message: '{_field_} can not be empty'
});

extend('max', {
    ...max,
    message: '{_field_} may not be greater than {length} characters'
});

extend('email', {
    ...email,
    message: 'Email must be valid'
});

export default {
    name: 'DetalheComarca',
    props: {
        comarca: {
            type: Object,
            required: false
        },
        showActionSalvar: Boolean
    },
    data() {
        return {
            isExtraSmall: false,
            isSmall: false,
            isMedium: false,
            show1: false,
            errorMessages: null,
            items: ['Item 1', 'Item 2', 'Item 3', 'Item 4'],
            id_user: '',
            ds_login: '',
            st_ativo: false,
            enabled: false,
            password: '',
            email: '',
            valid: null,
            createPassword: '',
            emailConfirmation: '',
            passwordRules: [
                v => !!v || 'Senha é requerida',
                v => v.length >= 2 || 'A senha deve ter 2 ou mais caracteres'
            ],
            passwordRulesConfirmation: [
                v => !!v || 'Senha é requerida',
                v => v == this.comarca.password || 'As senhas devem ser iguais'
            ],
            emailRulesConfirmation: [
                v => !!v || 'Email é requerido',
                v => v.length >= 2 || 'O e-mail deve ter 2 ou mais caracteres'
            ],
            nameRulesConfirmation: [
                v => !!v || 'Nome é requerido',
                v => v.length >= 2 || 'O nome deve ter 2 ou mais caracteres'
            ]
        };
    },
    methods: {
        focusInput($e) {
            $e.target.select();
        },
        aumentarLimite() {
            if (Number(this.comarca.ds_limite_diario) < 9999) {
                const soma = Number(this.comarca.ds_limite_diario) + 1;
                this.comarca.ds_limite_diario = String(soma);
            } else {
                this.comarca.ds_limite_diario = '9999';
            }
        },
        diminuirlimite() {
            let subtracao = Number(this.comarca.ds_limite_diario) - 1;
            if (subtracao < 0) {
                subtracao = 0;
            }
            this.comarca.ds_limite_diario = String(subtracao);
        },
        salvar() {
            this.$emit('actionSalvar');
        },
        handleBreakpoints(val) {
            this.isExtraSmall = false;
            this.isSmall = false;
            this.isMedium = false;
            if (val.xs) {
                this.isExtraSmall = true;
            } else if (val.sm) {
                this.isSmall = true;
            } else if (val.md) {
                this.isMedium = true;
            }
        },
        getIconSize() {
            if (this.isExtraSmall) {
                return '60';
            } else if (this.isSmall) {
                return '100';
            } else if (this.isMedium) {
                return '150';
            } else {
                return '150';
            }
        },
        keyupListener(e) {
            if (!e) e = window.event;
            const keyCode = e.code || e.key;

            if (keyCode === 'Escape') {
                this.$emit('fecharModal');
            }
        }
    },
    watch: {
        breakpoint: {
            deep: true,
            handler(val) {
                this.handleBreakpoints(val);
            }
        }
    },
    computed: {
        breakpoint() {
            return this.$vuetify.breakpoint;
        }
    },
    beforeDestroy() {
        document.removeEventListener('keyup', this.keyupListener);
    },
    mounted() {
        document.addEventListener('keyup', this.keyupListener);
        this.handleBreakpoints(this.breakpoint);
    }
};
</script>
